/**
 * Loads the name corpus once and hands out random picks.
 *
 * The corpus is a static JSON file built by scripts/build-names.mjs, fetched
 * lazily so it never weighs down the initial page load.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Gender, NameRecord } from '@/lib/types.ts'

export type Filter = 'any' | 'girl' | 'boy'

/**
 * How much more likely a name that charted in 2024 is to come up than an
 * obscure one. At 1 you get mostly names you've never heard of; at 20 you get
 * the same fifty over and over. Six is a good mix of familiar and surprising.
 */
const FAMILIARITY_BIAS = 6

const matches = (record: NameRecord, filter: Filter) =>
  filter === 'any' || record.gender === filter || record.gender === 'neutral'

export function useNames() {
  const [all, setAll] = useState<NameRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch('/data/names.json')
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status))
        return res.json() as Promise<{ names: NameRecord[] }>
      })
      .then(({ names }) => { if (!cancelled) setAll(names) })
      .catch(() => { if (!cancelled) setError('The list of names could not be loaded.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  return { all, loading, error }
}

/**
 * Weighted random selection, biased toward names in current use, and never
 * returning the same name twice in a row.
 */
export function usePicker(all: NameRecord[], filter: Filter) {
  const [current, setCurrent] = useState<NameRecord | null>(null)
  const lastRef = useRef<string | null>(null)

  const pool = useMemo(() => all.filter((n) => matches(n, filter)), [all, filter])

  const weights = useMemo(() => {
    let running = 0
    return pool.map((n) => {
      // Rank 1 is the most familiar name there is; rank 300 barely charts.
      const familiarity = n.rank ? 1 + (FAMILIARITY_BIAS - 1) * (1 - n.rank / 320) : 1
      running += Math.max(1, familiarity)
      return running
    })
  }, [pool])

  const draw = useCallback((): NameRecord | null => {
    if (pool.length === 0) return null
    const total = weights[weights.length - 1]
    for (let attempt = 0; attempt < 8; attempt++) {
      const target = Math.random() * total
      // Binary search the cumulative weights.
      let lo = 0
      let hi = weights.length - 1
      while (lo < hi) {
        const mid = (lo + hi) >> 1
        if (weights[mid] < target) lo = mid + 1
        else hi = mid
      }
      const picked = pool[lo]
      if (pool.length === 1 || picked.name !== lastRef.current) return picked
    }
    return pool[Math.floor(Math.random() * pool.length)]
  }, [pool, weights])

  const next = useCallback(() => {
    const picked = draw()
    if (picked) lastRef.current = picked.name
    setCurrent(picked)
  }, [draw])

  /** Put a specific name on the card — used when one is typed in by hand. */
  const select = useCallback((record: NameRecord) => {
    lastRef.current = record.name
    setCurrent(record)
  }, [])

  // A fresh name whenever the page loads or the filter changes.
  useEffect(() => { if (pool.length > 0) next() }, [pool, next])

  return { current, next, select, poolSize: pool.length }
}

export const genderWord: Record<Gender, string> = {
  girl: 'for a girl',
  boy: 'for a boy',
  neutral: 'for anyone',
}

/**
 * Tidy up a hand-typed name without flattening deliberate capitalisation.
 *
 * "eloise" and "ELOISE" both become "Eloise", but "McKenna" and "O'Brien" are
 * left exactly as written — nobody types a capital in the middle by accident.
 */
export function tidyName(raw: string): string {
  const trimmed = raw.trim().replace(/\s+/g, ' ')
  if (!trimmed) return ''
  const base = trimmed === trimmed.toUpperCase() ? trimmed.toLowerCase() : trimmed
  if (base !== base.toLowerCase()) return trimmed
  return base.replace(/(^|[\s'’-])([a-z])/g, (_, sep: string, ch: string) => sep + ch.toUpperCase())
}

/**
 * Look names up by hand.
 *
 * A typed name that we already know about arrives complete with its origin,
 * meaning and ranking — the point of typing one in is not to get a bare
 * string back, it's to see what we have on it.
 */
export function useLookup(all: NameRecord[], filter: Filter) {
  const byLower = useMemo(
    () => new Map(all.map((record) => [record.name.toLowerCase(), record])),
    [all],
  )

  /** The corpus entry for a name, or a fresh custom record if we don't have it. */
  const resolve = useCallback(
    (raw: string): NameRecord | null => {
      const name = tidyName(raw)
      if (!name) return null
      const known = byLower.get(name.toLowerCase())
      if (known) return known
      // Not one of ours. Guess the gender from whatever they're browsing.
      return { name, gender: filter === 'any' ? 'neutral' : filter, custom: true }
    },
    [byLower, filter],
  )

  /** Up to `limit` corpus names for a partial query: prefixes first, then contains. */
  const suggest = useCallback(
    (query: string, limit = 6): NameRecord[] => {
      const q = query.trim().toLowerCase()
      if (q.length < 2) return []

      const byRank = (a: NameRecord, b: NameRecord) =>
        (a.rank ?? 9999) - (b.rank ?? 9999) || a.name.localeCompare(b.name)

      const prefix: NameRecord[] = []
      const contains: NameRecord[] = []
      for (const record of all) {
        const lower = record.name.toLowerCase()
        if (lower === q) continue
        if (lower.startsWith(q)) prefix.push(record)
        else if (prefix.length < limit && lower.includes(q)) contains.push(record)
      }
      return [...prefix.sort(byRank), ...contains.sort(byRank)].slice(0, limit)
    },
    [all],
  )

  return { resolve, suggest }
}
